import React, { useState, useEffect } from 'react'
import { teacherStudentService } from '../../services/teacher/TeacherStudentService'
import { TeacherStatistics, StudentDetailStats, ClassStats, LessonStats, QuizStats } from '../../types/teacher'
import { useAuth } from '../../contexts/AuthContext'
import {
  Users,
  BookOpen,
  Target,
  Clock,
  TrendingUp,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  GraduationCap,
  FileText,
  Award,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import toast from 'react-hot-toast'

type StatsTab = 'overview' | 'students' | 'classes' | 'lessons' | 'quizzes' | 'games'

const TeacherStats: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<TeacherStatistics | null>(null)
  const [activeTab, setActiveTab] = useState<StatsTab>('overview')
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'linked' | 'invited'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedStudents, setExpandedStudents] = useState<Set<string>>(new Set())
  
  // ✅ NEW: Filters
  const [selectedClass, setSelectedClass] = useState<string>('')
  const [selectedLesson, setSelectedLesson] = useState<string>('')
  const [contentType, setContentType] = useState<'all' | 'theory' | 'quiz'>('all')

  // ✅ NEW: Auto-load stats when teacher logs in
  useEffect(() => {
    if (user?.role === 'teacher') {
      console.log('👨‍🏫 Teacher logged in, auto-loading statistics...')
      loadStats()
    }
  }, [user?.id, user?.role]) // Reload when user changes

  useEffect(() => {
    if (user?.role === 'teacher') {
      loadStats()
    }
  }, [selectedClass, selectedLesson, contentType])

  const loadStats = async () => {
    try {
      setLoading(true)
      
      // ✅ Lấy thống kê chi tiết từ API mới với filters
      const statistics = await teacherStudentService.getStatistics({
        class: selectedClass || undefined,
        lesson: selectedLesson || undefined,
        contentType: contentType
      })
      setStats(statistics)
    } catch (error: any) {
      console.error('Error loading teacher stats:', error)
      toast.error(error?.message || 'Không thể tải thống kê học sinh')
    } finally {
      setLoading(false)
    }
  }

  const toggleStudentExpanded = (email: string) => {
    const newExpanded = new Set(expandedStudents)
    if (newExpanded.has(email)) {
      newExpanded.delete(email)
    } else {
      newExpanded.add(email)
    }
    setExpandedStudents(newExpanded)
  }

  const filteredStudents = stats?.byStudent.filter(student => {
    // Filter by status
    if (selectedFilter !== 'all' && student.status !== selectedFilter) {
      return false
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        student.studentName.toLowerCase().includes(query) ||
        student.studentEmail.toLowerCase().includes(query) ||
        student.className.toLowerCase().includes(query) ||
        (student.school && student.school.toLowerCase().includes(query))
      )
    }
    return true
  }) || []

  const filteredClasses = stats?.byClass.filter(classItem => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return classItem.className.toLowerCase().includes(query)
    }
    return true
  }) || []

  const filteredLessons = stats?.byLesson.filter(lesson => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return lesson.lessonId.toLowerCase().includes(query)
    }
    return true
  }) || []

  const filteredQuizzes = stats?.byQuiz.filter(quiz => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return quiz.quizId.toLowerCase().includes(query)
    }
    return true
  }) || []

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải thống kê...</p>
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-800 mb-2">Không có dữ liệu</h3>
        <p className="text-gray-600 mb-6">Chưa có học sinh nào trong danh sách.</p>
        <button
          onClick={loadStats}
          className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Tải lại</span>
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-blue-500" />
            <span className="text-3xl font-bold text-gray-800">{stats.overview.totalStudents}</span>
          </div>
          <h3 className="text-gray-600 text-sm">Tổng số học sinh</h3>
          <p className="text-xs text-gray-500 mt-1">
            {stats.overview.linkedStudents} đã liên kết
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-2">
            <BookOpen className="w-8 h-8 text-green-500" />
            <div className="text-right">
              <span className="text-3xl font-bold text-gray-800">{stats.overview.totalLessonsCompleted}</span>
              {stats.overview.totalLessons > 0 && (
                <span className="text-lg text-gray-500">/{stats.overview.totalLessons}</span>
              )}
            </div>
          </div>
          <h3 className="text-gray-600 text-sm">Bài học hoàn thành</h3>
          <p className="text-xs text-gray-500 mt-1">
            {stats.overview.totalLessons > 0 
              ? `${Math.round((stats.overview.totalLessonsCompleted / stats.overview.totalLessons) * 100)}% hoàn thành`
              : 'Tổng cộng'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-8 h-8 text-purple-500" />
            <span className="text-3xl font-bold text-gray-800">{stats.overview.totalQuizzesCompleted}</span>
          </div>
          <h3 className="text-gray-600 text-sm">Bài trắc nghiệm</h3>
          <p className="text-xs text-gray-500 mt-1">
            Điểm TB: {stats.overview.averageScore > 0 ? stats.overview.averageScore.toFixed(1) : '—'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 text-orange-500" />
            <span className="text-2xl font-bold text-gray-800">
              {Math.floor(stats.overview.totalStudyTime / 3600)}h {Math.floor((stats.overview.totalStudyTime % 3600) / 60)}p
            </span>
          </div>
          <h3 className="text-gray-600 text-sm">Thời gian học</h3>
          <p className="text-xs text-gray-500 mt-1">Tổng cộng</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="flex border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Tổng quan</span>
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'students'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Theo học sinh ({stats.byStudent.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('classes')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'classes'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <GraduationCap className="w-4 h-4" />
            <span>Theo lớp ({stats.byClass.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('lessons')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'lessons'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Theo bài ({stats.byLesson.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('quizzes')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'quizzes'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Target className="w-4 h-4" />
            <span>Theo trắc nghiệm ({stats.byQuiz.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('games')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'games'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Award className="w-4 h-4" />
            <span>Theo game ({stats.byGame?.length || 0})</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* ✅ NEW: Advanced Filters */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Lọc theo:</label>
              </div>
              
              {/* Class Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Lớp:</label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[150px]"
                >
                  <option value="">Tất cả lớp</option>
                  {stats?.byClass.map((classItem) => (
                    <option key={classItem.className} value={classItem.className}>
                      {classItem.className}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lesson Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Bài:</label>
                <select
                  value={selectedLesson}
                  onChange={(e) => setSelectedLesson(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[200px]"
                >
                  <option value="">Tất cả bài</option>
                  {stats?.byLesson.map((lesson) => (
                    <option key={lesson.lessonId} value={lesson.lessonId}>
                      {lesson.lessonId}
                    </option>
                  ))}
                </select>
              </div>

              {/* Content Type Filter (Lý thuyết/Trắc nghiệm) */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Nội dung:</label>
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value as 'all' | 'theory' | 'quiz')}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[150px]"
                >
                  <option value="all">Tất cả</option>
                  <option value="theory">Lý thuyết</option>
                  <option value="quiz">Trắc nghiệm</option>
                </select>
              </div>

              {/* Status Filter (for students tab) */}
              {(activeTab === 'students' || activeTab === 'overview') && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">Trạng thái:</label>
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value as any)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="all">Tất cả</option>
                    <option value="linked">Đã liên kết</option>
                    <option value="invited">Chờ học sinh</option>
                  </select>
                </div>
              )}

              {/* Clear Filters Button */}
              {(selectedClass || selectedLesson || contentType !== 'all') && (
                <button
                  onClick={() => {
                    setSelectedClass('')
                    setSelectedLesson('')
                    setContentType('all')
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Xóa bộ lọc
                </button>
              )}
            </div>
          </div>

          {/* Filters and Search */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={
                    activeTab === 'students' ? 'Tìm theo tên, email, lớp...' :
                    activeTab === 'classes' ? 'Tìm theo tên lớp...' :
                    activeTab === 'lessons' ? 'Tìm theo ID bài học...' :
                    activeTab === 'quizzes' ? 'Tìm theo ID trắc nghiệm...' :
                    'Tìm kiếm...'
                  }
                  className="border border-gray-300 rounded-lg pl-3 pr-3 py-2 text-sm w-64"
                />
              </div>
            </div>
            <button
              onClick={loadStats}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Tải lại</span>
            </button>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top Students */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-500" />
                    Học sinh xuất sắc (Top 5)
                  </h4>
                  <div className="space-y-2">
                    {stats.byStudent
                      .filter(s => s.totalQuizzes > 0)
                      .sort((a, b) => b.averageScore - a.averageScore)
                      .slice(0, 5)
                      .map((student, index) => (
                        <div key={student.studentEmail} className="flex items-center justify-between bg-white p-3 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-gray-400 w-6">{index + 1}</span>
                            <div>
                              <div className="font-medium text-sm">{student.studentName}</div>
                              <div className="text-xs text-gray-500">{student.className}</div>
                            </div>
                          </div>
                          <span className="font-bold text-green-600">{student.averageScore.toFixed(1)}</span>
                        </div>
                      ))}
                    {stats.byStudent.filter(s => s.totalQuizzes > 0).length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">Chưa có dữ liệu</p>
                    )}
                  </div>
                </div>

                {/* Class Performance */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-500" />
                    Thành tích theo lớp
                  </h4>
                  <div className="space-y-2">
                    {stats.byClass
                      .sort((a, b) => b.averageScore - a.averageScore)
                      .slice(0, 5)
                      .map((classItem) => (
                        <div key={classItem.className} className="flex items-center justify-between bg-white p-3 rounded-lg">
                          <div>
                            <div className="font-medium text-sm">{classItem.className}</div>
                            <div className="text-xs text-gray-500">
                              {classItem.studentsWithActivity}/{classItem.totalStudents} học sinh
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-blue-600">{classItem.averageScore.toFixed(1)}</div>
                            <div className="text-xs text-gray-500">{classItem.totalLessonsCompleted} bài</div>
                          </div>
                        </div>
                      ))}
                    {stats.byClass.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">Chưa có dữ liệu</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Students Tab */}
          {activeTab === 'students' && (
            <div className="space-y-4">
              {filteredStudents.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  Không có học sinh nào phù hợp với bộ lọc.
                </div>
              ) : (
                filteredStudents.map((student) => (
                  <div key={student.studentEmail} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div 
                      className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                      onClick={() => toggleStudentExpanded(student.studentEmail)}
                    >
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <div className="font-medium text-gray-900">{student.studentName}</div>
                          <div className="text-xs text-gray-500">{student.studentEmail}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">{student.className}</div>
                          <div className="text-xs text-gray-500">{student.school || '—'}</div>
                        </div>
                        <div className="text-sm">
                          <span className="text-green-600 font-medium">{student.completedLessons}</span>
                          <span className="text-gray-500">/{student.totalLessons} bài</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">{student.totalQuizzes}</span>
                          <span className="text-gray-500"> trắc nghiệm</span>
                          {student.averageScore > 0 && (
                            <div className={`text-xs font-semibold ${
                              student.averageScore >= 8 ? 'text-green-600' :
                              student.averageScore >= 6 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              Điểm TB: {student.averageScore.toFixed(1)}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          {Math.floor(student.totalStudyTime / 3600)}h {Math.floor((student.totalStudyTime % 3600) / 60)}p
                        </div>
                      </div>
                      <div className="ml-4">
                        {expandedStudents.has(student.studentEmail) ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {expandedStudents.has(student.studentEmail) && (
                      <div className="border-t border-gray-200 p-4 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Lessons Detail */}
                          <div>
                            <h5 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                              <BookOpen className="w-4 h-4" />
                              Bài học ({student.lessons.length})
                            </h5>
                            {student.lessons.length > 0 ? (
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {student.lessons.map((lesson, idx) => {
                                  // ✅ Format thời gian đến giây
                                  const formatTimeDetailed = (seconds: number) => {
                                    const hours = Math.floor(seconds / 3600)
                                    const minutes = Math.floor((seconds % 3600) / 60)
                                    const secs = seconds % 60
                                    if (hours > 0) {
                                      return `${hours}h ${minutes}p ${secs}s`
                                    } else if (minutes > 0) {
                                      return `${minutes}p ${secs}s`
                                    } else {
                                      return `${secs}s`
                                    }
                                  }
                                  
                                  return (
                                    <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                          <div className="font-medium text-sm text-gray-900">{lesson.lessonId}</div>
                                          {lesson.completedAt && (
                                            <div className="text-xs text-gray-500 mt-0.5">
                                              Hoàn thành: {new Date(lesson.completedAt).toLocaleDateString('vi-VN')}
                                            </div>
                                          )}
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ml-2 ${
                                          lesson.status === 'completed' 
                                            ? 'bg-green-100 text-green-800' 
                                            : lesson.status === 'in_progress'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-gray-100 text-gray-800'
                                        }`}>
                                          {lesson.status === 'completed' ? 'Hoàn thành' : 
                                           lesson.status === 'in_progress' ? 'Đang học' : 
                                           'Chưa bắt đầu'}
                                        </span>
                                      </div>
                                      <div className="mt-2">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs text-gray-600">Tiến độ</span>
                                          <span className="text-xs font-semibold text-gray-800">{lesson.progress.toFixed(0)}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                          <div 
                                            className={`h-2 rounded-full transition-all ${
                                              lesson.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                                            }`}
                                            style={{ width: `${Math.min(lesson.progress, 100)}%` }}
                                          />
                                        </div>
                                      </div>
                                      {/* ✅ NEW: Thống kê chi tiết phần lý thuyết */}
                                      {lesson.theoryStats && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                          <div className="text-xs font-semibold text-gray-700 mb-2">Thống kê lý thuyết:</div>
                                          <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div className="flex items-center gap-1">
                                              <span className="text-green-600 font-semibold">✓</span>
                                              <span className="text-gray-600">Đúng: {lesson.theoryStats.correctAnswers}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <span className="text-red-600 font-semibold">✗</span>
                                              <span className="text-gray-600">Sai: {lesson.theoryStats.wrongAnswers}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <span className="text-yellow-600 font-semibold">💡</span>
                                              <span className="text-gray-600">Gợi ý: {lesson.theoryStats.hintsUsed}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <span className="text-purple-600 font-semibold">🤖</span>
                                              <span className="text-gray-600">AI: {lesson.theoryStats.aiQuestionsAsked}</span>
                                            </div>
                                          </div>
                                          <div className="mt-1 text-xs text-gray-500">
                                            Tổng: {lesson.theoryStats.totalQuestions} câu • Điểm: {lesson.theoryStats.score.toFixed(1)}%
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          {formatTimeDetailed(lesson.timeSpent)}
                                        </span>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500">Chưa có bài học nào</p>
                            )}
                          </div>

                          {/* Games Detail */}
                          {student.games && student.games.length > 0 && (
                            <div>
                              <h5 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                                <Award className="w-4 h-4" />
                                Game ({student.games.length})
                              </h5>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {student.games.map((game, idx) => (
                                  <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900">{game.gameId}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">{game.lessonId}</div>
                                        {game.lastPlayedAt && (
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            Lần cuối: {new Date(game.lastPlayedAt).toLocaleDateString('vi-VN')}
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <div className="text-xs text-gray-600">
                                          <span className="text-green-600 font-semibold">{game.wins}</span>
                                          <span className="text-gray-400">/</span>
                                          <span className="text-red-600 font-semibold">{game.losses}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">{game.totalAttempts} lần</div>
                                      </div>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                          <span className="text-gray-600">Điểm TB: </span>
                                          <span className="font-semibold text-blue-600">{game.averageScore.toFixed(1)}%</span>
                                        </div>
                                        <div>
                                          <span className="text-gray-600">Tốt nhất: </span>
                                          <span className="font-semibold text-green-600">{game.bestScore}</span>
                                        </div>
                                        <div>
                                          <span className="text-gray-600">Thời gian TB: </span>
                                          <span className="font-semibold">{Math.floor(game.averageTime / 60)}p {game.averageTime % 60}s</span>
                                        </div>
                                        <div>
                                          <span className="text-gray-600">Tốt nhất: </span>
                                          <span className="font-semibold text-blue-600">
                                            {Math.floor(game.bestTime / 60)}p {game.bestTime % 60}s
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Quizzes Detail */}
                          <div>
                            <h5 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                              <Target className="w-4 h-4" />
                              Trắc nghiệm ({student.quizzes.length})
                            </h5>
                            {student.quizzes.length > 0 ? (
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {student.quizzes.map((quiz, idx) => {
                                  // ✅ Format thời gian đến giây
                                  const formatTimeDetailed = (seconds: number) => {
                                    const hours = Math.floor(seconds / 3600)
                                    const minutes = Math.floor((seconds % 3600) / 60)
                                    const secs = seconds % 60
                                    if (hours > 0) {
                                      return `${hours}h ${minutes}p ${secs}s`
                                    } else if (minutes > 0) {
                                      return `${minutes}p ${secs}s`
                                    } else {
                                      return `${secs}s`
                                    }
                                  }
                                  
                                  return (
                                    <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                          <div className="font-medium text-sm text-gray-900">{quiz.quizId}</div>
                                          {quiz.completedAt && (
                                            <div className="text-xs text-gray-500 mt-0.5">
                                              Hoàn thành: {new Date(quiz.completedAt).toLocaleDateString('vi-VN')}
                                            </div>
                                          )}
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ml-2 ${
                                          quiz.percentage >= 80 ? 'bg-green-100 text-green-800' :
                                          quiz.percentage >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {quiz.percentage.toFixed(0)}%
                                        </span>
                                      </div>
                                      {/* ✅ NEW: Thống kê chi tiết trắc nghiệm */}
                                      <div className="mt-2 pt-2 border-t border-gray-200">
                                        <div className="text-xs font-semibold text-gray-700 mb-2">Kết quả:</div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                          <div className="flex items-center gap-1">
                                            <span className="text-green-600 font-semibold">✓</span>
                                            <span className="text-gray-600">Đúng: {quiz.correctAnswers}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-red-600 font-semibold">✗</span>
                                            <span className="text-gray-600">Sai: {quiz.wrongAnswers}</span>
                                          </div>
                                        </div>
                                        <div className="mt-1 text-xs text-gray-500">
                                          Tổng: {quiz.totalQuestions} câu • Điểm: {quiz.score.toFixed(1)}
                                        </div>
                                      </div>
                                      <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          {formatTimeDetailed(quiz.timeSpent)}
                                        </span>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500">Chưa có trắc nghiệm nào</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Classes Tab */}
          {activeTab === 'classes' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Lớp</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Số học sinh</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Có hoạt động</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bài học</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Trắc nghiệm</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Điểm TB</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Thời gian</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredClasses.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                        Không có lớp nào phù hợp.
                      </td>
                    </tr>
                  ) : (
                    filteredClasses.map((classItem) => (
                      <tr key={classItem.className} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{classItem.className}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{classItem.totalStudents}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{classItem.studentsWithActivity}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{classItem.totalLessonsCompleted}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{classItem.totalQuizzesCompleted}</td>
                        <td className="px-4 py-3 text-sm">
                          {classItem.averageScore > 0 ? (
                            <span className={`font-semibold ${
                              classItem.averageScore >= 8 ? 'text-green-600' :
                              classItem.averageScore >= 6 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {classItem.averageScore.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {Math.floor(classItem.totalStudyTime / 3600)}h {Math.floor((classItem.totalStudyTime % 3600) / 60)}p
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Lessons Tab */}
          {activeTab === 'lessons' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bài học</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Số học sinh</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Hoàn thành</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tỷ lệ hoàn thành</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Thời gian</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLessons.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                        Không có bài học nào phù hợp.
                      </td>
                    </tr>
                  ) : (
                    filteredLessons.map((lesson) => (
                      <tr key={lesson.lessonId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 text-sm">{lesson.lessonId}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{lesson.totalStudents}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{lesson.completedStudents}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all"
                                style={{ width: `${lesson.averageProgress}%` }}
                              />
                            </div>
                            <span className="font-medium text-blue-600 w-12 text-right">{lesson.averageProgress.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {Math.floor(lesson.totalTimeSpent / 3600)}h {Math.floor((lesson.totalTimeSpent % 3600) / 60)}p
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Games Tab */}
          {activeTab === 'games' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Game</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bài học</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Số lần chơi</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Thắng</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Thua</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Điểm TB</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Thời gian TB</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Thời gian tốt nhất</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(!stats.byGame || stats.byGame.length === 0) ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                        Chưa có dữ liệu game nào.
                      </td>
                    </tr>
                  ) : (
                    stats.byGame.map((game) => (
                      <tr key={game.gameId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 text-sm">{game.gameId}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{game.lessonId}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{game.totalAttempts}</td>
                        <td className="px-4 py-3 text-sm text-green-600 font-semibold">{game.totalWins}</td>
                        <td className="px-4 py-3 text-sm text-red-600 font-semibold">{game.totalLosses}</td>
                        <td className="px-4 py-3 text-sm">
                          {game.averageScore > 0 ? (
                            <span className={`font-semibold ${
                              game.averageScore >= 80 ? 'text-green-600' :
                              game.averageScore >= 60 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {game.averageScore.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {game.averageTime > 0 ? (
                            <span>{Math.floor(game.averageTime / 60)}p {game.averageTime % 60}s</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {game.bestTime > 0 ? (
                            <span className="font-semibold text-blue-600">
                              {Math.floor(game.bestTime / 60)}p {game.bestTime % 60}s
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Quizzes Tab */}
          {activeTab === 'quizzes' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Trắc nghiệm</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Số lần làm</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Điểm TB</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Câu đúng TB</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tổng câu hỏi</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredQuizzes.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                        Không có trắc nghiệm nào phù hợp.
                      </td>
                    </tr>
                  ) : (
                    filteredQuizzes.map((quiz) => (
                      <tr key={quiz.quizId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 text-sm">{quiz.quizId}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{quiz.totalAttempts}</td>
                        <td className="px-4 py-3 text-sm">
                          {quiz.averageScore > 0 ? (
                            <span className={`font-semibold ${
                              quiz.averageScore >= 8 ? 'text-green-600' :
                              quiz.averageScore >= 6 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {quiz.averageScore.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {quiz.averageCorrectAnswers > 0 ? (
                            <span className="font-medium">{quiz.averageCorrectAnswers.toFixed(1)}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {quiz.totalQuestions > 0 ? (
                            <span>{quiz.totalQuestions}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TeacherStats
